import OnlineAppComponent from "../lib/online_app_component";
import Component from "../../lib/component";
import {getSubmarkerOverlayClass} from "./submarker_overlay";
import {OnlineAppRootConfig} from "./online_app_root/online_app_root";
import {Templator, TemplatorConfig} from "../../components/templator/templator";
import {XHRFetcher} from "../../lib/xhr_fetcher";
// Standard currentColor spinner, imported as text (esbuild `.svg: text` loader) so it's inlined
// at build time and kept in sync with the asset — see the "SVG Asset Pattern" in the js-components
// skill. Inlined (not <img>) so its `currentColor` inherits the container colour and its built-in
// animation runs.
import iconSpinnerSvg from "./assets/icon__spinner.svg";

// The wrapper element is `submarker_infobox`, NOT `submarker`: the fetched fragment renders
// the shared Handlebars template whose root IS `.submarker` (the native card), so the wrapper
// must use a different class to avoid nesting `.submarker > … > .submarker`. The wrapper carries
// the map InfoBox concerns (marker pointer, open state, size); the injected `.submarker` is the card.
export const OnlineAppSubmarkerConfig = {
    baseHtmlClass: 'submarker_infobox',
    // The single visible framed element inside the wrapper. It owns the chrome (border/radius/
    // shadow/background) and the clip (`overflow: hidden`), holds the spinner + injected card, and
    // morphs its width/height between the loading pill and the measured card size. The wrapper stays
    // unclipped so the marker pointer (`:before`) can sit below the frame without being clipped.
    frameHtmlClass: 'submarker_infobox__frame',
    spinnerHtmlClass: 'submarker_infobox__spinner',
    spinnerSelector: '.submarker_infobox__spinner',
    // The fetched card's root element (the shared native `.submarker`), measured to size the frame.
    cardSelector: '.submarker'
}

// Placeholder in the `submarker_path` URL template (from the online-app config) that the
// map substitutes with the tapped experience's id. See app/helpers/online/apps_helper.rb.
const SUBMARKER_EXPERIENCE_ID_PLACEHOLDER = '__EXPERIENCE_ID__';

// Nominal width of the shared submarker card (matches `.submarker` width in
// app/stylesheets/online/apps/shared/submarker/_submarker.scss). Used only as a fallback when the
// rendered card can't be measured — the frame is normally sized from the measured card (Phase 2).
const SUBMARKER_CARD_WIDTH = 240;

// Duration of the pill->card morph (frame size + chrome). Must be >= the spinner's fade-out
// transition (250ms on `.submarker_infobox__spinner` in _submarker.scss) so the spinner has fully
// faded before it's removed, and >= the frame's width/height transition so the InfoBox width is
// re-pinned (for auto-pan) only after the size has settled.
const SUBMARKER_MORPH_MS = 300;

// Vertical InfoBox offset: lifts the box so the pointer PNG sits over the marker.
const SUBMARKER_POINTER_OFFSET_Y = -7;

export class OnlineAppSubmarker extends OnlineAppComponent {

    constructor(id_or_element, onlineApp) {
        super(id_or_element, onlineApp, OnlineAppSubmarkerConfig);
        this.currentExperienceMarker = null;
        this.currentSize = null;
        this.settled = false; // true once the morph has settled: centring switches from CSS transform to pixelOffset
        this.fragmentCache = {}; // experienceId -> { html, width, height } (avoids re-fetching + re-measuring on re-tap)
        this.loadingObserver = null; // observes the Templator for render-complete, to drive the morph
        this.revealTimer = null; // post-morph settle timer (drops the spent spinner + re-pins the InfoBox width)
        this.element = Component.createElement(OnlineAppSubmarkerConfig.baseHtmlClass);
        // The frame is a persistent child of the wrapper: content (spinner/card) is injected into
        // the frame, never into the wrapper directly, so the wrapper's `:before` pointer and state
        // classes are never clobbered by an innerHTML swap.
        this.frameElement = Component.createElement(OnlineAppSubmarkerConfig.frameHtmlClass);
        this.element.appendChild(this.frameElement);
        this.infoBoxOptions = this.getDefaultInfoBoxOptions();
        // Custom OverlayView replacing the archived InfoBox utility (see submarker_overlay.js). The
        // class is fetched via the factory because it can only be defined once google.maps is loaded.
        const SubmarkerOverlay = getSubmarkerOverlayClass();
        this.infoBox = new SubmarkerOverlay(this.infoBoxOptions);

        this.resizeTicking = false;
        this.onlineApp.env.addEventListener('resize', this.handleResize.bind(this));

        this.element.addEventListener('click', (event) => {
            event.preventDefault();
            this.dispatchAction('subMarkerClicked', this);
        });

        this.render();
    }

    handleResize(event) {
        if (!this.resizeTicking) {
            requestAnimationFrame((timestamp) => {
                this.resizeTicking = false;
                this.render();
            });
        };
        this.resizeTicking = true;
    }

    // Options consumed by SubmarkerOverlay (submarker_overlay.js) — the same shape the archived InfoBox
    // took. `alignBottom` is implicit (always true for the submarker), and `closeBoxURL` is unused.
    getDefaultInfoBoxOptions() {
        return {
            disableAutoPan: false,
            alignBottom: true,
            pixelOffset: new google.maps.Size(0, 0),
            infoBoxClearance: new google.maps.Size(16, 60),
            closeBoxURL: ''
        };
    }

    // Fetch the per-tap submarker fragment for the experience and render it via the shared
    // Handlebars template (the same template + data the native apps use). The fragment is a
    // thin templator-config wrapper; the `.hbs` template is fetched once and browser-cached.
    setElementContent(experienceMarker) {
        const experienceId = experienceMarker.experienceId;

        // Cancel any pending settle timer / observer from a previous tap, and clear stale content +
        // state (drops the pointer + any prior morph's inline frame size) before the new content.
        this.clearRevealTimer();
        this.disconnectLoadingObserver();
        this.resetFrameState();

        // Always start from the small loading pill, so the box always transitions (morphs) up to the
        // card size — near-instantly on a cache hit (the Templator's async render gives the paint gap
        // that lets the size transition run from the pill), after the fetch on a miss.
        //
        // The spinner shows only on a cache MISS: a cache hit reveals the card in ~40ms, so a spinner
        // would just flash. (Gating it on a time-delay doesn't work — the spinner is inserted while
        // `--loading` is already on, so its initial computed opacity is 1 and a fresh element renders
        // at that value with no transition. So we decide up front, deterministically, by cache state.)
        const cachedEntry = this.fragmentCache[experienceId];
        this.renderLoading({ spinner: cachedEntry === undefined });

        if (cachedEntry !== undefined) {
            this.injectFragment(cachedEntry);
            return;
        }

        const url = this.submarkerUrlForExperienceId(experienceId);
        if (!url) {
            console.error('[OnlineAppSubmarker] No submarker_path configured; cannot load submarker.');
            return;
        }

        new XHRFetcher(url, { responseType: 'text' }).fetch()
            .then((html) => {
                const entry = { html: html }; // dims filled in after the card is measured (revealWhenRendered)
                this.fragmentCache[experienceId] = entry;
                // Only inject if this experience is still the selected one (guards against a
                // newer tap resolving first).
                if (this.isStillSelected(experienceId)) {
                    this.injectFragment(entry);
                }
            })
            .catch((error) => {
                console.error('[OnlineAppSubmarker] Failed to load submarker fragment:', error);
                if (this.isStillSelected(experienceId)) {
                    this.renderError();
                }
            });
    }

    isStillSelected(experienceId) {
        return !!this.currentExperienceMarker && this.currentExperienceMarker.experienceId === experienceId;
    }

    // Loading shown on every tap: the frame is sized to a compact pill (via `--loading`) and
    // `populated` shows the pointer; the frame then morphs to the measured card size. The spinner is
    // included only when `options.spinner` (cache miss) — a cache hit reveals so fast the spinner
    // would only flash, so it's omitted and the pill just morphs.
    renderLoading(options = {}) {
        this.addHtmlClassState('loading');
        this.addHtmlClassState('populated');
        this.frameElement.innerHTML = (options.spinner === false)
            ? ''
            : `<span class="${ OnlineAppSubmarkerConfig.spinnerHtmlClass }">${ iconSpinnerSvg }</span>`;
        this.applyInfoBoxContent();
    }

    // Fallback if the fragment fetch fails, so the box doesn't sit empty.
    renderError() {
        this.addHtmlClassState('loading');
        this.addHtmlClassState('error');
        this.addHtmlClassState('populated');
        this.frameElement.innerHTML = `<span class="submarker_infobox__error">Couldn’t load — tap again.</span>`;
        this.applyInfoBoxContent();
    }

    // Clear the frame's content and reset all transient state classes. Dropping `populated` hides
    // the pointer so it never shows alone before there's content; clearing `loaded` returns the
    // frame to its pill size for the next tap.
    resetFrameState() {
        this.removeHtmlClassState('loading');
        this.removeHtmlClassState('loaded');
        this.removeHtmlClassState('error');
        this.removeHtmlClassState('populated');
        this.settled = false;
        this.removeHtmlClassState('settled'); // restore transform-centring for the next pill/morph
        this.frameElement.removeAttribute('style'); // drop any inline width/height from a prior morph
        this.frameElement.innerHTML = '';
    }

    submarkerUrlForExperienceId(experienceId) {
        const template = this.onlineApp && this.onlineApp.submarkerPath;
        if (!template) { return null; }
        return template.replace(SUBMARKER_EXPERIENCE_ID_PLACEHOLDER, experienceId);
    }

    // Inject the fetched fragment into the frame and instantiate the Templator on the injected
    // `.templator` node (added dynamically, so it isn't covered by the page-load component scan).
    //
    // The pill is always showing (setElementContent calls renderLoading first), so the card is
    // inserted beneath the spinner (which is `position:absolute` and stays layered on top until it
    // fades). The frame is sized to the card and revealed once the card has rendered + been measured
    // (revealWhenRendered) — that size change morphs the frame up from the pill.
    injectFragment(entry) {
        const spinner = this.frameElement.querySelector(OnlineAppSubmarkerConfig.spinnerSelector);

        this.frameElement.insertAdjacentHTML('afterbegin', entry.html); // card first; spinner stays on top (absolute)
        this.addHtmlClassState('populated');

        const templatorNode = this.frameElement.querySelector(TemplatorConfig.selector);
        if (!templatorNode) {
            // No card node (unexpected) — reveal whatever was injected so the box isn't stuck loading.
            this.reveal(spinner);
            return;
        }

        new Templator(templatorNode);
        this.revealWhenRendered(templatorNode, entry, spinner);
    }

    // Measure the rendered card, size the frame to it (driving the morph), and reveal — once the
    // Templator signals render-complete (`templator--rendered`). The size + chrome + fades all start
    // together here so the morph is one coordinated motion (cache hit or miss alike).
    revealWhenRendered(templatorNode, entry, spinner) {
        const finish = () => {
            if (!entry.width) {
                const dims = this.measureCard(templatorNode);
                entry.width = dims.width;   // cache the measured dims for instant re-taps
                entry.height = dims.height;
            }
            this.applyFrameSize(entry); // pill -> card size: CSS transitions the frame (see _submarker.scss)
            this.reveal(spinner);
        };
        if (templatorNode.classList.contains('templator--rendered')) { finish(); return; }
        this.disconnectLoadingObserver();
        this.loadingObserver = new MutationObserver(() => {
            if (templatorNode.classList.contains('templator--rendered')) {
                this.disconnectLoadingObserver();
                finish();
            }
        });
        this.loadingObserver.observe(templatorNode, { attributes: true, attributeFilter: ['class'] });
    }

    // Measure the rendered card's border-box (240×320 today; variable once Phase 3 relaxes the fixed
    // card height). The card is `flex: 0 0 auto` so it keeps its natural size even while clipped by a
    // smaller pill frame, so it's measurable in place — no offscreen render needed.
    measureCard(templatorNode) {
        const card = templatorNode.querySelector(OnlineAppSubmarkerConfig.cardSelector);
        const rect = (card || this.frameElement).getBoundingClientRect();
        return {
            width: Math.round(rect.width) || SUBMARKER_CARD_WIDTH,
            height: Math.round(rect.height)
        };
    }

    // Size the frame's border-box to wrap the measured card edge-to-edge (card border-box + the
    // frame's own border), matching what a naturally shrink-wrapped frame would be. Setting an
    // explicit px size is what lets the pill->card size change CSS-transition.
    applyFrameSize(entry) {
        const cs = getComputedStyle(this.frameElement);
        const borderX = (parseFloat(cs.borderLeftWidth) || 0) + (parseFloat(cs.borderRightWidth) || 0);
        const borderY = (parseFloat(cs.borderTopWidth) || 0) + (parseFloat(cs.borderBottomWidth) || 0);
        this.frameElement.style.width = (entry.width + borderX) + 'px';
        this.frameElement.style.height = (entry.height + borderY) + 'px';
    }

    // Swap loading -> loaded so CSS runs the morph: the frame transitions to the card size and its
    // pill chrome to the card chrome, the spinner fades out, and the card fades in (its own
    // `.templator` fade). `populated` stays — the card is there.
    //
    // The InfoBox width is re-pinned (for auto-pan) and the spent spinner removed only AFTER the morph
    // settles: applyInfoBoxContent re-appends the element (InfoBox rewrites its container), which would
    // reset a running CSS transition if called mid-morph.
    reveal(spinner) {
        this.removeHtmlClassState('loading');
        this.addHtmlClassState('loaded');
        // Fade the spinner out (only present on a cache miss; it's been in the DOM long enough for the
        // opacity transition to run, unlike on a fresh insert).
        if (spinner) { spinner.classList.add('submarker_infobox__spinner--done'); }

        this.clearRevealTimer();
        this.revealTimer = setTimeout(() => {
            this.revealTimer = null;
            if (spinner && spinner.parentNode) { spinner.parentNode.removeChild(spinner); }
            this.settleCentering();
        }, SUBMARKER_MORPH_MS);
    }

    // Once the morph has settled, switch from transform-centring to InfoBox's own pixelOffset
    // centring: set the container's left offset to -(width/2) and drop the wrapper transform. Both are
    // applied in the same tick and describe the SAME visual position (the transform shifted the card
    // left by width/2; the offset now shifts the container left by width/2 instead), so there's no
    // jump — but now the InfoBox container sits exactly over the visible card. That fixes (a) the dead
    // zone where the un-shifted right half of the container blocked map interaction, and (b) auto-pan,
    // which uses the container's offset/width to reserve space. setOptions here re-runs panBox_ with
    // the corrected offset so a card near a map edge pans in correctly.
    settleCentering() {
        if (!this.element.offsetWidth) { return; } // not laid out (closed mid-timer); clearRevealTimer normally prevents this
        this.settled = true;
        this.addHtmlClassState('settled'); // CSS drops the wrapper transform (see _submarker.scss)
        this.applyInfoBoxContent();        // pixelOffset() now returns h = -(width/2) because `settled` is true
    }

    clearRevealTimer() {
        if (this.revealTimer) {
            clearTimeout(this.revealTimer);
            this.revealTimer = null;
        }
    }

    disconnectLoadingObserver() {
        if (this.loadingObserver) {
            this.loadingObserver.disconnect();
            this.loadingObserver = null;
        }
    }

    applyInfoBoxContent() {
        this.infoBox.setContent(this.element);
        this.infoBoxOptions.pixelOffset = this.pixelOffset();
        this.infoBox.setOptions(this.infoBoxOptions);
    }

    openInfoBox(experienceMarker){
        this.addHtmlClassState('info_box_open');

        const onlineAppMap = this.onlineApp?.map;
        if (!onlineAppMap) return;

        this.infoBox.open(onlineAppMap.googleMap, experienceMarker.googleMarker);

        if (this.readState('navOpen')) {
            // TODO - If (after the infoBox is open and may have panned) the marker is behind the nav then we offset the map to pan the marker out from behind the nav.
        }
    }

    closeInfoBox(){
        this.removeHtmlClassState('info_box_open');
        this.clearRevealTimer();
        this.resetFrameState();
        this.disconnectLoadingObserver();
        this.infoBox.close();
    }

    // The InfoBox pixelOffset (offset of the container from the marker pixel).
    //
    // While loading + morphing (`!settled`), horizontal centring is done in CSS via
    // `transform: translateX(-50%)` on the wrapper — it centres the box at ANY width, so the frame can
    // morph from the pill to the card with no per-frame width maths; `h` is 0 (container left edge at
    // the marker). Once settled, the transform is dropped (`--settled`) and centring is done HERE via
    // `h = -(width/2)`. Computing it in pixelOffset() (rather than a one-shot at settle) is what makes
    // it durable: EVERY redraw — our resize/tap re-render, or Google's own draw on zoom/pan/resize —
    // re-applies the correct offset, so the settled card can't snap back to left-anchored.
    pixelOffset() {
        const h = this.settled ? -Math.round(this.element.offsetWidth / 2) : 0;
        return new google.maps.Size(h, SUBMARKER_POINTER_OFFSET_Y);
    }

    renderSize() {
        this.removeHtmlClassState('small');
        this.removeHtmlClassState('medium');
        if (this.currentSize) { this.addHtmlClassState(this.currentSize) };
    }

    renderContent(experienceMarker=null) {
        if (experienceMarker && this.isDifferentExperienceMarker(experienceMarker)) {
            // New experience selected — fetch + render its fragment. injectFragment() applies
            // the InfoBox content/offset once the fragment is in place (it may be async).
            this.setElementContent(experienceMarker);
        } else {
            // Same experience (e.g. a resize re-render) — just refresh content/offset in place.
            this.applyInfoBoxContent();
        }
    }

    isDifferentExperienceMarker(experienceMarker){
        return this.currentExperienceMarker !== experienceMarker
    }

    render() {
        const modeCode = this.readState('modal');
        this.currentSize = (modeCode === OnlineAppRootConfig.modalModeCode) ? 'small' : 'medium';
        this.renderSize();

        const experienceMarker = this.readState('experienceMarker');

        if (experienceMarker) {

            this.renderContent(experienceMarker);

            if (this.isDifferentExperienceMarker(experienceMarker)) {
                this.currentExperienceMarker = experienceMarker;
                this.openInfoBox(experienceMarker);
            }

        } else {
            this.closeInfoBox();
        }

        this.currentExperienceMarker = experienceMarker;
    }

}